import { Box, Button, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Timestamp, collection, doc, getDoc, setDoc } from 'firebase/firestore';
import { createIndex, removeIndexesOf } from '../../utils/indexer';
import { useNavigate, useParams } from 'react-router-dom';

import { Organization } from '../../types';
import { db } from "../../firebase";
import useAuth from '../../hooks/useAuth';

interface OrganizationFormProps {
}

const OrganizationForm: React.FC<OrganizationFormProps> = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization>({
    id: '',
    name: '',
    description: '',
    contact: '',
    email: '',
    updatedAt: Timestamp.now(),
    updatedBy: user?.display_name ?? 'anonymous',
  });
  const [originalName, setOriginalName] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchOrganization = async (id: string) => {
    try {
      const orgRef = doc(db, 'organizations', id);
      const orgSnap = await getDoc(orgRef);
      
      if (orgSnap.exists()) {
        const fetchedOrg = orgSnap.data() as Organization;
        const updatedOrg = {
          id: orgSnap.id,
          name: fetchedOrg.name,
          description: fetchedOrg.description ?? '',
          contact: fetchedOrg.contact ?? '',
          email: fetchedOrg.email ?? '',
          updatedAt: fetchedOrg.updatedAt ?? Timestamp.now(),
          updatedBy: fetchedOrg.updatedBy ?? (user?.display_name ?? 'anonymous'),
        };
        setOrganization(updatedOrg);
        setOriginalName(fetchedOrg.name); // Store the original name
      } else {
        setError('Organization not found');
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      setError('Error fetching organization');
    }
  };

  useEffect(() => {
    if (id) {
      fetchOrganization(id);
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      if (id) {
        const orgRef = doc(db, 'organizations', id);
        await setDoc(orgRef, {
          ...organization,
          updatedAt: Timestamp.now(),
          updatedBy: user?.display_name ?? 'anonymous',
        });

        // Only recreate indexes if the name has changed
        if (organization.name !== originalName) {
          await removeIndexesOf("organization", "name", `organizations/${id}`);
          await createIndex("organization", "name", organization.name, `organizations/${id}`, user?.display_name ?? "anonymous");
        }
      } else {
        const orgsRef = collection(db, "organizations");
        const newOrgRef = doc(orgsRef);
        await setDoc(newOrgRef, {
          ...organization,
          id: newOrgRef.id,
          updatedAt: Timestamp.now(),
          updatedBy: user?.display_name ?? 'anonymous',
        });
        await createIndex("organization", "name", organization.name, newOrgRef.path, user?.display_name ?? "anonymous");
      }
      navigate('/organizations');
    } catch (error) {
      console.error('Error saving organization:', error);
      setError('Error saving organization');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setOrganization(prevOrg => ({
      ...prevOrg,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600, margin: 'auto' }}>
      <Typography variant="h4">{id ? 'Edit Organization' : 'Add Organization'}</Typography>
      {error && <Typography color="error">{error}</Typography>}
      <TextField
        label="Name"
        name="name"
        value={organization.name}
        onChange={handleChange}
        required
      />
      <TextField
        label="Description"
        name="description"
        value={organization.description}
        onChange={handleChange}
        multiline
        rows={4}
      />
      <TextField
        label="Contact Person"
        name="contact"
        value={organization.contact}
        onChange={handleChange}
      />
      <TextField
        label="Email"
        name="email"
        type="email"
        value={organization.email}
        onChange={handleChange}
      />
      <Button type="submit" variant="contained" disabled={isSaving}>
        {id ? 'Update Organization' : 'Add Organization'}
      </Button>
    </Box>
  );
};

export default OrganizationForm;
